import React, { useState } from 'react';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Server, CheckCircle2, AlertCircle, RotateCcw } from 'lucide-react';
import { getServerUrl, setServerUrl, clearServerUrl, probeServer } from '../utils/serverUrl';

/**
 * ServerUrlSetting
 *
 * Lets the user point the app at a self-hosted GhostComm backend.
 * Visible in Settings (desktop/web) AND on the APK's first-launch screen.
 */
export const ServerUrlSetting = () => {
  const current = getServerUrl();
  const buildDefault = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');
  const isCustom = current && current !== buildDefault;

  const [value, setValue] = useState(current);
  const [probing, setProbing] = useState(false);
  const [probeResult, setProbeResult] = useState(null); // {ok, version, error}

  const handleTest = async () => {
    setProbeResult(null);
    setProbing(true);
    try {
      const res = await probeServer(value);
      setProbeResult(res);
      if (!res.ok) toast.error(`Server unreachable: ${res.error}`);
    } finally {
      setProbing(false);
    }
  };

  const handleSave = async () => {
    try {
      const res = await probeServer(value);
      if (!res.ok) {
        toast.error(`Cannot save — server unreachable: ${res.error}`);
        setProbeResult(res);
        return;
      }
      setServerUrl(value);
      toast.success('Server URL saved. Reloading...');
      setTimeout(() => window.location.reload(), 700);
    } catch (e) {
      toast.error(e?.message || 'Invalid URL');
    }
  };

  const handleReset = () => {
    clearServerUrl();
    setValue(buildDefault);
    toast.success('Reverted to default server. Reloading...');
    setTimeout(() => window.location.reload(), 700);
  };

  return (
    <div className="p-4 rounded-lg bg-secondary/30 space-y-3">
      <div className="flex items-center gap-2 text-sky-400">
        <Server className="w-5 h-5" />
        <span className="font-medium">Backend Server</span>
        {isCustom && (
          <span className="text-xs px-2 py-0.5 rounded-full bg-sky-500/20 text-sky-300">Custom</span>
        )}
      </div>

      <p className="text-xs text-slate-500">
        Point the app at a self-hosted Government Shield server. All your E2E encryption still runs
        client-side — the server only sees encrypted blobs.
      </p>

      <Input
        value={value}
        onChange={(e) => {
          setValue(e.target.value);
          setProbeResult(null);
        }}
        placeholder="https://my-ghostcomm.example.com"
        className="bg-background/50 border-input focus:border-primary font-mono text-xs"
        autoComplete="off"
        spellCheck="false"
        data-testid="server-url-input"
      />

      {probeResult && (
        <div
          className={`flex items-start gap-2 text-xs p-2 rounded ${
            probeResult.ok
              ? 'text-emerald-300 bg-emerald-500/10 border border-emerald-500/20'
              : 'text-red-300 bg-red-500/10 border border-red-500/20'
          }`}
          data-testid="server-url-probe-result"
        >
          {probeResult.ok ? (
            <>
              <CheckCircle2 className="w-4 h-4 shrink-0 mt-0.5" />
              <span>Reachable — Government Shield v{probeResult.version}</span>
            </>
          ) : (
            <>
              <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
              <span>{probeResult.error}</span>
            </>
          )}
        </div>
      )}

      <div className="grid grid-cols-2 gap-2">
        <Button
          variant="secondary"
          onClick={handleTest}
          disabled={probing || !value.trim()}
          data-testid="server-url-test-btn"
        >
          {probing ? 'Testing...' : 'Test connection'}
        </Button>
        <Button
          onClick={handleSave}
          disabled={probing || !value.trim() || value.trim() === current}
          data-testid="server-url-save-btn"
        >
          Save & reload
        </Button>
      </div>

      {isCustom && (
        <button
          onClick={handleReset}
          className="text-xs text-slate-500 hover:text-slate-300 flex items-center gap-1"
          data-testid="server-url-reset-btn"
        >
          <RotateCcw className="w-3 h-3" />
          Reset to default ({buildDefault})
        </button>
      )}
    </div>
  );
};

export default ServerUrlSetting;
